import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { useHinweise } from '../hinweise/HinweisContext'
import { Karte } from '../komponenten/KarteLazy'
import { oeffneKarte } from '../komponenten/karte-oeffnen'

/**
 * Was zwischen Fahrer und Betrieb hin und her geht.
 *
 * Drei Bereiche in einer Seite, weil sie derselbe Mensch zur selben Zeit ansieht: Was ist
 * hereingekommen (Unfälle, Fahrzeugprobleme), und was muss hinaus (Nachrichten). Sie auf
 * drei Navigationspunkte zu verteilen hieße, dass eine dringende Unfallmeldung wartet,
 * während jemand auf der Nachrichtenseite steht.
 */

interface Anhang {
  id: number
  dateiname: string | null
  mimetyp: string | null
  groesseBytes: number
  bild: boolean
}

interface UnfallAntwort {
  id: number
  fahrerId: number
  fahrer: string
  personalnummer: string
  zeitpunkt: string
  gemeldetAm: string
  ort: string | null
  linie: string | null
  hergang: string | null
  fahrzeug: string | null
  personenschaden: boolean
  polizeiVorOrt: boolean
  fahrzeugFahrbereit: boolean
  beteiligte: string | null
  status: string
  dringend: boolean
  anhaenge: number
  dateien: Anhang[]
  breite: number | null
  laenge: number | null
  bearbeitungsnotiz: string | null
}

interface FahrzeugAntwort {
  id: number
  fahrzeugId: number
  fahrzeug: string
  fahrer: string
  personalnummer: string
  gemeldetAm: string
  art: string
  dringlichkeit: string
  beschreibung: string
  fahrbereit: boolean
  kilometerstand: number | null
  status: string
  dringend: boolean
  anhaenge: number
  dateien: Anhang[]
  bearbeitungsnotiz: string | null
}


const STATUS_TEXT: Record<string, string> = {
  OFFEN: 'offen',
  IN_BEARBEITUNG: 'in Bearbeitung',
  ABGESCHLOSSEN: 'abgeschlossen',
}

const STATUS_ETIKETT: Record<string, string> = {
  OFFEN: 'orange',
  IN_BEARBEITUNG: 'navy',
  ABGESCHLOSSEN: 'gruen',
}

const DRINGLICHKEIT_TEXT: Record<string, string> = {
  SOFORT: 'sofort',
  HEUTE: 'heute',
  GELEGENHEIT: 'bei Gelegenheit',
}

const ART_TEXT: Record<string, string> = {
  MANGEL: 'Mangel',
  SCHADEN: 'Schaden',
  VERSCHMUTZUNG: 'Verschmutzung',
  TECHNIK: 'Technik',
  SONSTIGES: 'Sonstiges',
}

function alsZeitpunkt(iso: string | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  return (
    d.toLocaleDateString('de-DE') +
    ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  )
}

type Bereich = 'eingang'

export function Meldungen() {
  const [bereich, setBereich] = useState<Bereich>('eingang')
  const [unfaelle, setUnfaelle] = useState<UnfallAntwort[]>([])
  const [fahrzeuge, setFahrzeuge] = useState<FahrzeugAntwort[]>([])
  const [nurOffene, setNurOffene] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [offen, setOffen] = useState<string | null>(null)

  const { aktualisiere: hinweiseNachziehen } = useHinweise()

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      const filter = nurOffene ? '?status=OFFEN' : ''
      setUnfaelle(await api.get<UnfallAntwort[]>(`/api/meldungen/unfaelle${filter}`))
      setFahrzeuge(await api.get<FahrzeugAntwort[]>(`/api/meldungen/fahrzeuge${filter}`))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }, [nurOffene])

  useEffect(() => {
    void laden()
  }, [laden])

  async function statusSetzen(pfad: string, id: number, status: string, notiz?: string) {
    try {
      await api.post(`/api/meldungen/${pfad}/${id}`, { status, notiz: notiz ?? null })
      await laden()
      // Angenommen oder abgeschlossen: Die Meldung zaehlt nicht mehr als offen.
      hinweiseNachziehen()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Fehlgeschlagen' })
    }
  }

  const dringende =
    unfaelle.filter((u) => u.dringend).length + fahrzeuge.filter((f) => f.dringend).length

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Meldungen</h1>
          <p>Was die Fahrer melden — und was sie erreichen soll.</p>
        </div>
        <div className="werkzeugleiste" style={{ gap: 8 }}>
          <button
            className={bereich === 'eingang' ? 'knopf-primaer' : 'knopf-schlicht'}
            onClick={() => setBereich('eingang')}
          >
            Eingang{dringende > 0 ? ` (${dringende} dringend)` : ''}
          </button>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {dringende > 0 && bereich === 'eingang' && (
        <div className="meldung fehler">
          {dringende === 1 ? 'Eine Meldung verlangt' : `${dringende} Meldungen verlangen`}{' '}
          sofortige Aufmerksamkeit: Personenschaden oder ein Fahrzeug, das nicht fahren darf.
        </div>
      )}

      {bereich === 'eingang' && (
        <>
          <div className="werkzeugleiste" style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={nurOffene}
                onChange={(e) => setNurOffene(e.target.checked)}
              />
              nur offene
            </label>
          </div>

          <div className="abschnitt">
            <h2>Unfallmeldungen</h2>
            <div className="tabelle-huelle">
              {laedt ? (
                <div className="laden">Wird geladen …</div>
              ) : unfaelle.length === 0 ? (
                <div className="leer">Keine Unfallmeldungen.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Zeitpunkt</th>
                      <th>Fahrer</th>
                      <th>Ort / Linie</th>
                      <th>Merkmale</th>
                      <th>Anhänge</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {unfaelle.map((u) => (
                      <tr key={u.id} style={u.dringend ? { background: 'var(--rot-100)' } : undefined}>
                        <td>{alsZeitpunkt(u.zeitpunkt)}</td>
                        <td>
                          <strong>{u.fahrer}</strong>
                          <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem' }}>
                            {u.personalnummer}
                            {u.fahrzeug ? ` · ${u.fahrzeug}` : ''}
                          </div>
                        </td>
                        <td>
                          {u.ort ?? <span style={{ color: 'var(--grau-500)' }}>ohne Angabe</span>}
                          {u.linie && (
                            <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem' }}>
                              Linie {u.linie}
                            </div>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {u.personenschaden && (
                            <div>
                              <span className="etikett rot">Personenschaden</span>
                            </div>
                          )}
                          {!u.fahrzeugFahrbereit && (
                            <div>
                              <span className="etikett rot">nicht fahrbereit</span>
                            </div>
                          )}
                          {u.polizeiVorOrt && (
                            <div>
                              <span className="etikett grau">Polizei</span>
                            </div>
                          )}
                        </td>
                        <td className="zahl">{u.anhaenge > 0 ? u.anhaenge : '–'}</td>
                        <td>
                          <span className={`etikett ${STATUS_ETIKETT[u.status] ?? 'grau'}`}>
                            {STATUS_TEXT[u.status] ?? u.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            className="knopf-schlicht"
                            style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                            onClick={() => setOffen(offen === `u${u.id}` ? null : `u${u.id}`)}
                          >
                            Hergang
                          </button>{' '}
                          {u.status !== 'ABGESCHLOSSEN' && (
                            <button
                              className="knopf-schlicht"
                              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                              onClick={() =>
                                void statusSetzen(
                                  'unfaelle',
                                  u.id,
                                  u.status === 'OFFEN' ? 'IN_BEARBEITUNG' : 'ABGESCHLOSSEN',
                                )
                              }
                            >
                              {u.status === 'OFFEN' ? 'Übernehmen' : 'Abschließen'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {unfaelle
              .filter((u) => offen === `u${u.id}`)
              .map((u) => (
                <div key={u.id} className="karte abschnitt">
                  <h3 style={{ marginTop: 0 }}>
                    Hergang — {u.fahrer}, {alsZeitpunkt(u.zeitpunkt)}
                  </h3>
                  <p style={{ whiteSpace: 'pre-wrap' }}>
                    {u.hergang ?? 'Der Fahrer hat keinen Hergang eingetragen.'}
                  </p>
                  {u.beteiligte && (
                    <p>
                      <strong>Beteiligte:</strong> {u.beteiligte}
                    </p>
                  )}
                  {u.breite != null && u.laenge != null && (
                    <div style={{ margin: '10px 0' }}>
                      <strong style={{ display: 'block', marginBottom: 6 }}>Standort</strong>
                      {/* Klein und ohne Bedienung: In einer Liste mehrerer Meldungen soll
                          die Karte zeigen, wo etwas war, nicht das Blättern abfangen. Wer
                          genauer hinsehen will, klickt sie auf. */}
                      <div style={{ maxWidth: 420 }}>
                        <Karte
                          klein
                          hoehe={170}
                          punkte={[{ breite: u.breite, laenge: u.laenge, betont: true }]}
                          beimKlicken={() =>
                            oeffneKarte({
                              breite: u.breite as number,
                              laenge: u.laenge as number,
                              titel: `Unfallort — ${u.fahrer}, ${alsZeitpunkt(u.zeitpunkt)}`,
                            })
                          }
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 5,
                            fontSize: '0.78rem',
                            color: 'var(--grau-500)',
                          }}
                        >
                          <span>
                            {u.breite.toFixed(5)}, {u.laenge.toFixed(5)}
                          </span>
                          <button
                            type="button"
                            className="textknopf"
                            onClick={() =>
                              oeffneKarte({
                                breite: u.breite as number,
                                laenge: u.laenge as number,
                                titel: `Unfallort — ${u.fahrer}, ${alsZeitpunkt(u.zeitpunkt)}`,
                              })
                            }
                          >
                            Größer anzeigen
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <Anhaenge dateien={u.dateien} />
                  <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                    Gemeldet {alsZeitpunkt(u.gemeldetAm)}
                  </p>
                </div>
              ))}
          </div>

          <div className="abschnitt">
            <h2>Fahrzeugprobleme</h2>
            <div className="tabelle-huelle">
              {laedt ? (
                <div className="laden">Wird geladen …</div>
              ) : fahrzeuge.length === 0 ? (
                <div className="leer">Keine Fahrzeugmeldungen.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Gemeldet</th>
                      <th>Fahrzeug</th>
                      <th>Was</th>
                      <th>Dringlichkeit</th>
                      <th>Anhänge</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {fahrzeuge.map((f) => (
                      <tr key={f.id} style={f.dringend ? { background: 'var(--rot-100)' } : undefined}>
                        <td>{alsZeitpunkt(f.gemeldetAm)}</td>
                        <td>
                          <strong>{f.fahrzeug}</strong>
                          <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem' }}>
                            {f.fahrer}
                          </div>
                        </td>
                        <td>
                          <span className="etikett grau">{ART_TEXT[f.art] ?? f.art}</span>
                          <div style={{ fontSize: '0.85rem', marginTop: 3 }}>{f.beschreibung}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {!f.fahrbereit ? (
                            <span className="etikett rot">nicht fahrbereit</span>
                          ) : (
                            <span
                              className={`etikett ${
                                f.dringlichkeit === 'SOFORT' ? 'orange' : 'grau'
                              }`}
                            >
                              {DRINGLICHKEIT_TEXT[f.dringlichkeit] ?? f.dringlichkeit}
                            </span>
                          )}
                        </td>
                        <td className="zahl">{f.anhaenge > 0 ? f.anhaenge : '–'}</td>
                        <td>
                          <span className={`etikett ${STATUS_ETIKETT[f.status] ?? 'grau'}`}>
                            {STATUS_TEXT[f.status] ?? f.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {f.status !== 'ABGESCHLOSSEN' && (
                            <button
                              className="knopf-schlicht"
                              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                              onClick={() =>
                                void statusSetzen(
                                  'fahrzeuge',
                                  f.id,
                                  f.status === 'OFFEN' ? 'IN_BEARBEITUNG' : 'ABGESCHLOSSEN',
                                )
                              }
                            >
                              {f.status === 'OFFEN' ? 'Übernehmen' : 'Abschließen'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}


    </>
  )
}

/** Das Formular zum Schreiben — an einzelne, an ein Team oder an alle. */

/**
 * Die Anhänge einer Meldung — Fotos sofort sichtbar, alles andere zum Herunterladen.
 *
 * Ein schlichtes `<img src="/api/dokumente/…">` genügt hier nicht: Das Token steht im
 * localStorage, nicht in einem Cookie, und der Browser schickt bei einem gewöhnlichen
 * Bildaufruf keinen Authorization-Kopf mit — der Server antwortet mit 401 und das Bild
 * bliebe leer. Deshalb der Umweg über einen Blob und einen Objekt-URL, der beim Verlassen
 * wieder freigegeben wird.
 */
function Anhaenge({ dateien }: { dateien: Anhang[] }) {
  const [bilder, setBilder] = useState<Record<number, string>>({})

  useEffect(() => {
    const erzeugte: string[] = []
    let abgebrochen = false

    void (async () => {
      for (const d of dateien.filter((x) => x.bild)) {
        try {
          const blob = await api.holeBlob(`/api/dokumente/${d.id}/datei`)
          if (abgebrochen) return
          const url = URL.createObjectURL(blob)
          erzeugte.push(url)
          setBilder((vorher) => ({ ...vorher, [d.id]: url }))
        } catch {
          // Ein Bild, das nicht lädt, ist kein Grund, die Meldung unbrauchbar zu machen.
        }
      }
    })()

    return () => {
      abgebrochen = true
      erzeugte.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [dateien])

  if (!dateien || dateien.length === 0) return null

  const fotos = dateien.filter((d) => d.bild)
  const andere = dateien.filter((d) => !d.bild)

  return (
    <div className="anhaenge">
      {fotos.length > 0 && (
        <div className="anhaenge-bilder">
          {fotos.map((d) =>
            bilder[d.id] ? (
              <a key={d.id} href={bilder[d.id]} target="_blank" rel="noreferrer">
                <img src={bilder[d.id]} alt={d.dateiname ?? 'Foto zur Meldung'} />
              </a>
            ) : (
              <div key={d.id} className="anhang-platzhalter">wird geladen …</div>
            ),
          )}
        </div>
      )}
      {andere.map((d) => (
        <button
          key={d.id}
          type="button"
          className="knopf knopf-schlicht"
          onClick={() =>
            void api.herunterladen(`/api/dokumente/${d.id}/datei`, d.dateiname ?? undefined)
          }
        >
          {d.mimetyp?.startsWith('audio/') ? '🎤 ' : '📎 '}
          {d.dateiname ?? 'Anhang'}
        </button>
      ))}
    </div>
  )
}
